import React from 'react';
import { Link } from 'react-router-dom';
import { GoogleAuthProvider, signInWithPopup, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import logoConnect10 from '../../assets/logo-connect10.png';
import { logLogin } from '../../services/loginLogService';

/**
 * @file Componente de página de login.
 * Renderiza o formulário de login com e-mail/senha e a opção de login com Google.
 * Também inclui a funcionalidade de redefinição de senha.
 *
 * @param {object} props - As propriedades do componente.
 * @param {object} props.auth - A instância do serviço de autenticação do Firebase.
 */
const LoginPage = ({ auth }) => {
    const [email, setEmail] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [error, setError] = React.useState('');
    const [successMessage, setSuccessMessage] = React.useState('');

    const handleGoogleSignIn = async () => {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({
            prompt: 'select_account'
        });
        try {
            const result = await signInWithPopup(auth, provider);
            if (result.user) {
                await logLogin(result.user);
            }
        } catch (error) {
            console.error("Erro no login com Google:", error);
            if (error.code === 'auth/account-exists-with-different-credential') {
                setError("Este e-mail já está cadastrado. Faça login com sua senha para vincular sua conta Google.");
            } else {
                setError("Não foi possível fazer login com o Google. Verifique se o pop-up não foi bloqueado.");
            }
        }
    };

    const handleEmailSignIn = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');
        if (!email || !password) {
            setError("Por favor, preencha e-mail e senha.");
            return;
        }
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            if (userCredential.user) {
                await logLogin(userCredential.user);
            }
        } catch (error) {
            console.error("Erro no login com e-mail:", error);
            if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
                setError("E-mail ou senha inválidos.");
            } else {
                setError("Ocorreu um erro ao tentar fazer login.");
            }
        }
    };

    const handlePasswordReset = async () => {
        setError('');
        setSuccessMessage('');
        if (!email) {
            setError("Por favor, digite seu e-mail para redefinir a senha.");
            return;
        }
        try {
            await sendPasswordResetEmail(auth, email);
            setSuccessMessage("Se o e-mail estiver cadastrado, um link para redefinir a senha foi enviado.");
        } catch (error) {
            console.error("Erro ao redefinir senha:", error);
            setError("Ocorreu um erro ao tentar redefinir a senha.");
        }
    };
    
    // Estilo padrão para os inputs, garantindo consistência visual
    const inputStyle = "w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:border-slate-600 dark:text-white";

    return (
        <div className="w-full min-h-screen bg-slate-100 dark:bg-slate-900 flex items-center justify-center p-4 font-sans">
            <div className="w-full max-w-md p-8 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl">
                <img src={logoConnect10} alt="Logo Connect10" className="h-12 mx-auto mb-6" />
                <h1 className="text-2xl font-bold text-center text-slate-800 dark:text-slate-100 mb-2">Acesso ao Dashboard</h1>
                <p className="text-center text-slate-500 dark:text-slate-400 mb-8">Utilize suas credenciais para entrar.</p>
                
                <form onSubmit={handleEmailSignIn}>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">E-mail</label>
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="seu.email@connect10.com.br" className={inputStyle} required />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Senha</label>
                        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputStyle} required />
                    </div>
                    <div className="text-right mb-6">
                        <button type="button" onClick={handlePasswordReset} className="text-sm font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300">
                            Esqueceu a senha?
                        </button>
                    </div>

                    {error && <p className="mb-4 text-center text-red-500 text-sm">{error}</p>}
                    {successMessage && <p className="mb-4 text-center text-green-500 text-sm">{successMessage}</p>}

                    <button type="submit" className="w-full bg-blue-600 text-white font-bold py-3 rounded-lg hover:bg-blue-700 transition-colors duration-300 flex items-center justify-center">
                        Entrar com E-mail
                    </button>
                </form>

                <div className="my-6 flex items-center">
                    <div className="flex-grow border-t border-slate-300 dark:border-slate-600"></div>
                    <span className="flex-shrink mx-4 text-slate-400 dark:text-slate-500 text-sm">OU</span>
                    <div className="flex-grow border-t border-slate-300 dark:border-slate-600"></div>
                </div>

                <button onClick={handleGoogleSignIn} className="w-full bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 font-semibold py-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-600 border border-slate-300 dark:border-slate-600 transition-colors duration-300 flex items-center justify-center">
                    <svg className="w-5 h-5 mr-3" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"></path><path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"></path><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"></path><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.447-2.274 4.488-4.386 5.958l6.19 5.238C42.012 36.49 44 30.836 44 24c0-1.341-.138-2.65-.389-3.917z"></path></svg>
                    Entrar com Google
                </button>

                <div className="mt-6 text-center">
                    <Link to="/signup" className="text-sm font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300">
                        Não tem uma conta? Cadastre-se
                    </Link>
                </div>

                <div className="mt-8 pt-4 border-t border-slate-200 dark:border-slate-700 text-center">
                    <a href="/privacidade" className="text-xs text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400 transition-colors">
                        Política de Privacidade
                    </a>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
